import { XMarkIcon } from '@heroicons/react/20/solid';
import Image from 'next/image';
import {
  HTMLInputTypeAttribute,
  useEffect,
  useMemo,
  useState,
} from 'react';
import DatePicker from 'react-datepicker';
import { cs } from 'date-fns/locale/cs';
import { de } from 'date-fns/locale/de';
import { enUS as en } from 'date-fns/locale/en-US';
import { hu } from 'date-fns/locale/hu';
import { pl } from 'date-fns/locale/pl';
import { sk } from 'date-fns/locale/sk';
import { format } from 'date-fns';
import type { Locale } from 'date-fns';
import { useTranslation } from 'next-i18next/pages';
import moment from 'moment';
import {
  returnCalendarOpensAt,
  returnCalendarMinDate,
  canReturnOn,
} from 'utils/returnCalendarMonth';

// Calendar language for each site language that can be enabled
// (assets/localization/config.json, by the language of its localeWithRegion).
// Imported one by one: a require() built from a variable made webpack bundle
// every date-fns locale into the shared chunk, over 2 MB of source. Looking the
// site code up directly was also wrong for some sites: "ca" (Canada) gave
// Catalan dates. Every other site language is English.
const calendarLocales: Record<string, Locale> = {
  hu,
  sk,
  cz: cs,
  pl,
  de,
  at: de,
  ch: de,
};

const calendarLocaleFor = (language: string) =>
  calendarLocales[language] || calendarLocales[language.split('-')[0]] || en;

interface IDatePickerProps {
  className?: string;
  fromDate: Date | null;
  toDate: Date | null;
  onFromDateChange: (date: Date | null) => void;
  onToDateChange: (date: Date | null) => void;
  onEnter: () => void;
  type?: HTMLInputTypeAttribute;
  icon?: string;
  placeholderFrom?: string;
  placeholderTo?: string;
  deleteReturnDate?: () => void;
  openToDate: boolean;
  setOpenToDate: (val: boolean) => void;
  extraDays?: boolean;
}

export const CustomDatePicker: React.FC<IDatePickerProps> = ({
  className,
  fromDate,
  toDate,
  onFromDateChange,
  onToDateChange,
  onEnter,
  type,
  icon,
  deleteReturnDate,
  openToDate,
  setOpenToDate,
  extraDays,
  placeholderFrom = '',
  placeholderTo = '',
}) => {
  const { t, i18n } = useTranslation();
  const onKeyDown: React.KeyboardEventHandler<HTMLInputElement> = e => {
    if (e.key === 'Enter') {
      onEnter();
    }
  };
  // Worked out during render, so the server already sends the dates in the
  // page's language instead of English that switches after load.
  const datePickerLocale = calendarLocaleFor(i18n.language || 'en');

  // WHIT-493: two months stacked were taller than a phone screen, so part of
  // the calendar was always off screen or under the sticky header. Phones
  // get one month; an opened calendar is scrolled fully into view (its
  // scroll margin in DatePicker.css clears the header).
  const [monthsShown, setMonthsShown] = useState(2);
  useEffect(() => {
    const phone = window.matchMedia('(max-width: 639px)');
    const update = () => setMonthsShown(phone.matches ? 1 : 2);
    update();
    phone.addEventListener('change', update);
    return () => phone.removeEventListener('change', update);
  }, []);
  // By its own class: both calendars can be in the page at once.
  const revealCalendar = (popperClassName: string) =>
    requestAnimationFrame(() =>
      document
        .querySelector(`.${popperClassName}`)
        ?.scrollIntoView({ block: 'nearest', behavior: 'smooth' }),
    );
  // The return calendar is opened by its parent (openToDate), not by a click.
  useEffect(() => {
    if (openToDate) revealCalendar('return-date-picker-popper');
  }, [openToDate]);

  // The first month the departure calendar showed when the departure was
  // picked, so the return calendar can open on the same two months (WHIT-494,
  // utils/returnCalendarMonth.js): the picked day's month, or the month
  // before when it sat in the right-hand column. Read from the clicked day,
  // because the calendar reports a "month change" to the picked day's month
  // itself; a day picked with the keyboard counts as the left-hand column.
  const [departureViewStart, setDepartureViewStart] = useState<Date>();
  const noteDepartureView = (d: Date | null, event?: React.SyntheticEvent) => {
    if (!d) return;
    const month = (event?.target as Element | undefined)?.closest?.(
      '.react-datepicker__month-container',
    );
    const column = month?.parentElement
      ? Array.from(
          month.parentElement.querySelectorAll(
            ':scope > .react-datepicker__month-container',
          ),
        ).indexOf(month)
      : 0;
    setDepartureViewStart(
      new Date(d.getFullYear(), d.getMonth() - Math.max(column, 0), 1),
    );
  };
  const returnOpensAt = returnCalendarOpensAt(
    fromDate,
    toDate,
    departureViewStart,
  );

  const renderHeader = (
    monthDate: Date,
    customHeaderCount: number,
    decreaseMonth: () => void,
    increaseMonth: () => void,
    directionType: string,
  ) => {
    let left = '';
    let right = '';

    switch (directionType) {
      case 'start':
        left = 'bg-tt-light-purple';
        right = 'bg-tt-verylight-purple';
        break;
      case 'end':
        left = 'bg-tt-verylight-purple';
        right = 'bg-tt-light-purple';
        break;
    }

    return (
      <div>
        <div
          className={`invisible sm:visible h-0 sm:h-10 flex items-center ${
            customHeaderCount == 0 ? 'justify-end' : 'justify-start'
          }`}>
          <div
            className={`h-1 w-40 ${
              customHeaderCount == 0
                ? `${left} rounded-l-full`
                : `${right} rounded-r-full`
            }`}
          />
        </div>
        <div
          className={`sm:invisible h-10 sm:h-0 flex items-center ${
            customHeaderCount == 0 ? 'visible justify-center' : 'invisible'
          }`}>
          <div className={`h-1 w-40 ${left}`} />
          <div className={`h-1 w-40 ${right}`} />
        </div>
        <div className="relative">
          <button
            aria-label={t('Previous month') || ''}
            className={`react-datepicker__navigation react-datepicker__navigation--previous`}
            style={
              customHeaderCount === 1 ? { visibility: 'hidden' } : undefined
            }
            onClick={decreaseMonth}>
            <span
              className={
                'react-datepicker__navigation-icon react-datepicker__navigation-icon--previous'
              }>
              {'<'}
            </span>
          </button>
          <span className="react-datepicker__current-month">
            {/* Same locale as the calendar: Intl does not know the site
                code "cz" and would print the month in English. */}
            {format(monthDate, 'LLLL', { locale: datePickerLocale })}
          </span>
          <button
            aria-label={t('Next month') || ''}
            className={`react-datepicker__navigation react-datepicker__navigation--next
              ${customHeaderCount == 1 && 'invisible sm:visible'}
              ${customHeaderCount == 0 && 'visible sm:invisible'}`}
            onClick={increaseMonth}>
            <span
              className={
                'react-datepicker__navigation-icon react-datepicker__navigation-icon--next'
              }>
              {'>'}
            </span>
          </button>
        </div>
      </div>
    );
  };

  // No title: it showed a debugging string ("Tooltip for date: Thu Nov 26
  // 2026 00:00:00 GMT+0100 …") over every day (WHIT-494).
  const renderDayContents = (day: number, date: Date | undefined) => {
    return (
      <div className="react-datepicker__day--custom-wrapper">
        <span
          className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
          {date ? new Date(date).getDate().toLocaleString() : ''}
        </span>
        <div className="absolute react-datepicker__day--overlay"></div>
      </div>
    );
  };

  const dayClassName = (date: Date) => {
    const lastDayOfMonth = new Date(date.getFullYear(), date.getMonth() + 1, 0);
    const firstDayOfMonth = new Date(date.getFullYear(), date.getMonth(), 1);
    if (date.getDay() == 0 || date.getTime() === lastDayOfMonth.getTime()) {
      return 'rounded-right dm-sans';
    } else if (
      date.getDay() == 1 ||
      date.getTime() === firstDayOfMonth.getTime()
    ) {
      return 'rounded-left dm-sans';
    } else {
      return 'dm-sans';
    }
  };

  const highlightDates = useMemo<any[]>(() => {
    if (extraDays) {
      const dates = [];
      for (const date of [fromDate, toDate]) {
        if (date == null) continue;
        for (let index = -3; index <= 3; index++) {
          dates.push(moment(date).add(index, 'days').toDate());
        }
      }
      //For overlapping extraDays ranges
      const dLength = dates.length;
      if (toDate != null && fromDate != null) {
        if (moment(toDate).diff(fromDate, 'days') <= 7) {
          //Dates overlapping
          return [
            {
              'react-datepicker__day--extra-days': dates.slice(1, dLength - 1),
            },
            {
              'react-datepicker__day--extra-days react-datepicker__day--extra-days-start':
                [dates[0]],
            },
            {
              'react-datepicker__day--extra-days react-datepicker__day--extra-days-end':
                [dates[dLength - 1]],
            },
          ];
        } else {
          //Dates not overlapping
          return [
            {
              'react-datepicker__day--extra-days': [
                ...dates.slice(1, 6),
                ...dates.slice(7, dates.length - 1),
              ],
            },
            {
              'react-datepicker__day--extra-days react-datepicker__day--extra-days-start':
                [dates[0], dates[7]],
            },
            {
              'react-datepicker__day--extra-days react-datepicker__day--extra-days-end':
                [dates[6], dates[dLength - 1]],
            },
          ];
        }
      } else {
        //No end date was selected
        return [
          { 'react-datepicker__day--extra-days': [...dates.slice(1, 6)] },
          {
            'react-datepicker__day--extra-days react-datepicker__day--extra-days-start':
              [dates[0]],
          },
          {
            'react-datepicker__day--extra-days react-datepicker__day--extra-days-end':
              [dates[6]],
          },
        ];
      }
    } else {
      return [];
    }
  }, [fromDate, toDate, extraDays]);

  return (
    <div
      id="datePickerContainer"
      className="flex w-full justify-center xl:justify-end">
      <div
        id="startDate"
        className={`${className} flex-1 hover:border-tt-dark-purple flex pl-3 rounded h-12 border rounded-r-none border-r-[0.5px] focus:border-2 focus:border-tt-dark-purple hover:border-2 hover:-mx-[1px] ${
          !fromDate ? 'border-tt-field-empty-border bg-tt-verylight-purple' : ''
        }  w-full`}>
        {icon && (
          <Image
            style={{ color: undefined }}
            src={icon}
            className="h-6 self-center justify-center w-min mr-3"
            alt=""
          />
        )}
        <DatePicker
          renderCustomHeader={({
            monthDate,
            customHeaderCount,
            decreaseMonth,
            increaseMonth,
          }) =>
            renderHeader(
              monthDate,
              customHeaderCount,
              decreaseMonth,
              increaseMonth,
              'start',
            )
          }
          calendarClassName="bg-slate-700"
          renderDayContents={(day, date) => renderDayContents(day, date)}
          dayClassName={dayClassName}
          selected={fromDate}
          onChange={(d, event) => {
            noteDepartureView(d, event);
            onFromDateChange(d);
          }}
          selectsStart
          startDate={fromDate}
          endDate={toDate}
          minDate={new Date()}
          monthsShown={monthsShown}
          onCalendarOpen={() => revealCalendar('start-date-picker-popper')}
          popperClassName="start-date-picker-popper"
          placeholderText={placeholderFrom}
          className="placeholder-tt-date-placeholder"
          shouldCloseOnSelect
          calendarStartDay={1}
          dateFormat="MMMM d."
          locale={datePickerLocale}
          highlightDates={highlightDates}
        />
      </div>
      <div
        id="endDate"
        className={`${className} flex-1 hover:border-tt-dark-purple flex pr-3 rounded h-12 border rounded-l-none hover:border-2 focus:border-2 focus:border-tt-dark-purple hover:-mx-[1px] ${
          !toDate ? 'border-tt-field-empty-border bg-tt-verylight-purple' : ''
        } w-full`}>
        <DatePicker
          renderCustomHeader={({
            monthDate,
            customHeaderCount,
            decreaseMonth,
            increaseMonth,
          }) =>
            renderHeader(
              monthDate,
              customHeaderCount,
              decreaseMonth,
              increaseMonth,
              'end',
            )
          }
          renderDayContents={(day, date) => renderDayContents(day, date)}
          dayClassName={dayClassName}
          selected={toDate}
          onChange={onToDateChange}
          selectsEnd
          startDate={fromDate}
          endDate={toDate}
          minDate={returnCalendarMinDate(fromDate, returnOpensAt)}
          filterDate={day => canReturnOn(day, fromDate)}
          monthsShown={monthsShown}
          onCalendarOpen={() => revealCalendar('return-date-picker-popper')}
          popperClassName="return-date-picker-popper"
          placeholderText={placeholderTo}
          className="placeholder-tt-date-placeholder pl-3"
          calendarStartDay={1}
          dateFormat="MMMM d."
          disabled={!fromDate}
          openToDate={returnOpensAt}
          onClickOutside={() => setOpenToDate(false)}
          onInputClick={() => setOpenToDate(true)}
          open={openToDate}
          locale={datePickerLocale}
          highlightDates={highlightDates}
        />
        {/* A button, not a submit; 44px wide to tap on a phone, the icon
            where it was (WHIT-494). */}
        <button
          type="button"
          className="flex shrink-0 items-center justify-center w-11 h-full -mr-3"
          aria-label={t('Clear return date') || ''}
          onClick={() => deleteReturnDate && deleteReturnDate()}>
          <XMarkIcon className="w-5 h-5 text-tt-light-gray-purple-line" />
        </button>
      </div>
    </div>
  );
};
